import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Users as UsersIcon,
  UserCheck,
  UserX,
  UserPlus,
  ShieldCheck,
  Star,
  Mail,
  Phone,
  Calendar,
  Eye,
  X,
  CreditCard,
  Briefcase,
  Heart,
  Film,
  Loader2,
  RefreshCw,
  ArrowUpDown,
  Filter,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Activity,
  Users2,
  Zap,
  Crown,
  Clock,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useUserList, useUserDetail } from '../hooks/useUsers';
import { useUsersAnalytics } from '../hooks/useAnalytics';
import { UserListQueryParams, UserListItem } from '../types/user';
import { AnalyticsDateRange, TrendPoint } from '../types/analytics';
import {
  cn,
  formatRelativeTime,
  formatDateTime,
  formatNumber,
  formatCents,
  formatPhoneNumber,
  formatPercent,
  formatCompactNumber,
  parseDateOnly,
} from '../utils/helpers';
import { Avatar, Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

// ============================================================================
// Date Presets (reusable from Dashboard pattern)
// ============================================================================

type DatePreset = '7d' | '30d' | '90d' | 'all';

const presets: { label: string; value: DatePreset }[] = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'All time', value: 'all' },
];

function getDateRange(preset: DatePreset): AnalyticsDateRange | undefined {
  if (preset === 'all') return undefined;
  const end = new Date();
  const start = new Date();
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

// ============================================================================
// Filter Types
// ============================================================================

interface UserFilters {
  search?: string;
  isActive?: string;
  isVerified?: string;
  isProvider?: string;
  hasStripeAccount?: string;
  createdAfter?: string;
  createdBefore?: string;
  sortBy?: 'createdAt' | 'lastSeenAt' | 'name' | 'rating';
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// KPI Stat Card (matching Dashboard pattern exactly)
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
  subtitle?: string;
  trend?: { value: number; label: string } | null;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  loading,
  subtitle,
  trend,
  onClick,
}) => (
  <div className={cn('card p-4 overflow-hidden', onClick && 'cursor-pointer hover:ring-1 hover:ring-border transition-shadow')} onClick={onClick}>
    <div className="flex items-start gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xl font-bold text-foreground">{value}</p>
            {trend && (
              <div
                className={cn(
                  'flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-md whitespace-nowrap',
                  trend.value >= 0
                    ? 'text-emerald-400 bg-emerald-400/10'
                    : 'text-red-400 bg-red-400/10'
                )}
              >
                {trend.value >= 0 ? (
                  <ArrowUp className="h-2.5 w-2.5" />
                ) : (
                  <ArrowDown className="h-2.5 w-2.5" />
                )}
                <span>{Math.abs(trend.value)}%</span>
              </div>
            )}
          </div>
        )}
        {subtitle && !loading && (
          <p className="text-[10px] text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// SVG Area Chart for User Growth
// ============================================================================

interface AreaChartProps {
  data: TrendPoint[];
  loading?: boolean;
  color?: string;
  height?: number;
}

const AreaChart: React.FC<AreaChartProps> = ({
  data,
  loading,
  color = '#2AC5F4',
  height = 160,
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  if (loading) {
    return (
      <div className="flex items-end gap-0.5" style={{ height }}>
        {Array.from({ length: 14 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted/20 rounded-t animate-pulse"
            style={{ height: `${20 + Math.random() * 60}%` }}
          />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height }}
      >
        No trend data available
      </div>
    );
  }

  const values = data.map((d) => d.count);
  const max = Math.max(...values, 1);
  const padding = 4;
  const chartWidth = 400;
  const chartHeight = height - 24; // leave room for labels

  // Generate smooth path
  const points = values.map((v, i) => ({
    x: padding + (i / (values.length - 1 || 1)) * (chartWidth - padding * 2),
    y: chartHeight - padding - (v / max) * (chartHeight - padding * 2),
  }));

  const pathD = points
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ');

  const areaD = `${pathD} L ${points[points.length - 1].x} ${chartHeight - padding} L ${points[0].x} ${chartHeight - padding} Z`;

  // Show ~5 evenly spaced date labels with consistent gaps
  const labelIndices: number[] = [];
  const labelCount = Math.min(5, data.length);
  if (labelCount <= 1) {
    labelIndices.push(0);
  } else {
    // Distribute gaps evenly: each gap is either `base` or `base+1`,
    // with larger gaps spread to the front for visual balance
    const totalGap = data.length - 1;
    const gaps = labelCount - 1;
    const base = Math.floor(totalGap / gaps);
    const remainder = totalGap % gaps;
    let idx = 0;
    for (let i = 0; i < labelCount; i++) {
      labelIndices.push(idx);
      idx += base + (i < remainder ? 1 : 0);
    }
  }

  // Compute tooltip position in percentages for the hovered point
  const hoveredPoint = hovered !== null ? points[hovered] : null;
  const tooltipLeftPct = hoveredPoint ? (hoveredPoint.x / chartWidth) * 100 : 0;
  const tooltipTopPct = hoveredPoint ? (hoveredPoint.y / height) * 100 : 0;

  return (
    <div style={{ height }} className="relative" ref={containerRef}>
      <svg
        viewBox={`0 0 ${chartWidth} ${height}`}
        className="w-full"
        preserveAspectRatio="none"
        style={{ height: '100%' }}
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const y = chartHeight - padding - frac * (chartHeight - padding * 2);
          return (
            <line
              key={frac}
              x1={padding}
              y1={y}
              x2={chartWidth - padding}
              y2={y}
              stroke="currentColor"
              className="text-border"
              strokeWidth="0.5"
              strokeDasharray={frac === 0 ? 'none' : '4 4'}
              opacity={0.5}
            />
          );
        })}

        {/* Area fill */}
        <path d={areaD} fill="url(#areaGradient)" />

        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Vertical indicator line for hovered point */}
        {hovered !== null && points[hovered] && (
          <line
            x1={points[hovered].x}
            y1={padding}
            x2={points[hovered].x}
            y2={chartHeight - padding}
            stroke={color}
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity={0.5}
          />
        )}

        {/* Data points — visible dot for hovered, invisible hit areas for all */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r="4"
              fill={color}
              stroke="var(--color-card, #1a1a2e)"
              strokeWidth="2"
              opacity={hovered === i ? 1 : 0}
            />
            {/* Invisible wider hit area */}
            <circle
              cx={p.x}
              cy={p.y}
              r="14"
              fill="transparent"
              onMouseEnter={() => setHovered(i)}
            />
          </g>
        ))}
      </svg>

      {/* X-axis labels — rendered as HTML so they don't stretch with preserveAspectRatio="none" */}
      <div className="absolute bottom-0 left-0 right-0 h-5 pointer-events-none overflow-visible">
        {labelIndices.map((idx, i) => (
          <span
            key={idx}
            className={cn(
              "absolute text-[10px] text-muted-foreground whitespace-nowrap",
              i === 0 ? 'translate-x-0' : i === labelIndices.length - 1 ? '-translate-x-full' : '-translate-x-1/2'
            )}
            style={{ left: `${(points[idx]?.x / chartWidth) * 100}%` }}
          >
            {data[idx]?.date
              ? new Date(data[idx].date + 'T00:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
              : ''}
          </span>
        ))}
      </div>

      {/* Tooltip overlay */}
      {hovered !== null && data[hovered] && (
        <div
          className="absolute pointer-events-none z-10"
          style={{
            left: `${tooltipLeftPct}%`,
            top: `${tooltipTopPct}%`,
            transform: `translate(${tooltipLeftPct > 80 ? '-100%' : tooltipLeftPct < 20 ? '0%' : '-50%'}, -110%)`,
          }}
        >
          <div className="bg-card border border-border rounded-lg shadow-xl px-3 py-2 whitespace-nowrap">
            <p className="text-xs font-semibold text-foreground">{formatNumber(values[hovered])}<span className="text-muted-foreground font-normal"> users</span></p>
            <p className="text-[10px] text-muted-foreground">
              {new Date(data[hovered].date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SVG Mini Bar Chart (for simple metrics)
// ============================================================================

interface MiniBarChartProps {
  data: TrendPoint[];
  loading?: boolean;
  color?: string;
}

const MiniBarChart: React.FC<MiniBarChartProps> = ({
  data,
  loading,
  color = 'bg-primary',
}) => {
  if (loading) {
    return (
      <div className="flex items-end gap-0.5 h-16">
        {Array.from({ length: 14 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted/20 rounded-t animate-pulse"
            style={{ height: `${20 + Math.random() * 60}%` }}
          />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-16 text-xs text-muted-foreground">
        No trend data
      </div>
    );
  }

  const values = data.map((d) => d.count);
  const max = Math.max(...values, 1);

  return (
    <div className="flex items-end gap-0.5 h-16">
      {values.map((v, i) => (
        <div
          key={i}
          className={cn(
            'flex-1 rounded-t transition-all',
            color,
            'opacity-70 hover:opacity-100'
          )}
          style={{ height: `${Math.max((v / max) * 100, 4)}%` }}
          title={`${data[i].date}: ${formatNumber(v)}`}
        />
      ))}
    </div>
  );
};

// ============================================================================
// SVG Donut Chart for User Distribution
// ============================================================================

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
  loading?: boolean;
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
  centerValue?: string;
}

const DonutChart: React.FC<DonutChartProps> = ({
  segments,
  loading,
  size = 140,
  strokeWidth = 20,
  centerLabel,
  centerValue,
}) => {
  if (loading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <div
          className="rounded-full border-4 border-muted/30 animate-pulse"
          style={{ width: size - 20, height: size - 20 }}
        />
      </div>
    );
  }

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ width: size, height: size }}
      >
        No data
      </div>
    );
  }

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let cumulativeOffset = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments
          .filter((s) => s.value > 0)
          .map((seg, i) => {
            const segmentLength = (seg.value / total) * circumference;
            const offset = cumulativeOffset;
            cumulativeOffset += segmentLength;

            return (
              <circle
                key={i}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${center} ${center})`}
                className="transition-all duration-500"
              >
                <title>{`${seg.label}: ${formatNumber(seg.value)} (${((seg.value / total) * 100).toFixed(1)}%)`}</title>
              </circle>
            );
          })}
        {/* Center text */}
        {centerValue && (
          <>
            <text
              x={center}
              y={center - 6}
              textAnchor="middle"
              className="fill-foreground text-lg font-bold"
              fontSize="18"
            >
              {centerValue}
            </text>
            {centerLabel && (
              <text
                x={center}
                y={center + 12}
                textAnchor="middle"
                className="fill-muted-foreground"
                fontSize="10"
              >
                {centerLabel}
              </text>
            )}
          </>
        )}
      </svg>
      {/* Legend */}
      <div className="space-y-1.5">
        {segments
          .filter((s) => s.value > 0)
          .map((seg, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: seg.color }}
              />
              <span className="text-xs text-muted-foreground">{seg.label}</span>
              <span className="text-xs font-medium text-foreground">
                {formatNumber(seg.value)}
              </span>
              <span className="text-[10px] text-muted-foreground/60">
                ({((seg.value / total) * 100).toFixed(0)}%)
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};

// ============================================================================
// Progress Bar Component
// ============================================================================

interface ProgressBarProps {
  label: string;
  value: number;
  total: number;
  color?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  label,
  value,
  total,
  color = 'bg-emerald-500',
}) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-32 flex-shrink-0">
        {label}
      </span>
      <div className="flex-1 h-2 rounded-full bg-muted/20 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-foreground w-16 text-right">
        {formatNumber(value)}{' '}
        <span className="text-muted-foreground/60">({pct.toFixed(0)}%)</span>
      </span>
    </div>
  );
};

// ============================================================================
// User Status Badges
// ============================================================================

const UserStatusBadges: React.FC<{ user: UserListItem }> = ({ user }) => (
  <div className="flex flex-wrap gap-1">
    {user.isActive ? (
      <Badge variant="success" size="sm">
        Active
      </Badge>
    ) : (
      <Badge variant="error" size="sm">
        Inactive
      </Badge>
    )}
    {user.isVerified && (
      <Badge variant="info" size="sm">
        Verified
      </Badge>
    )}
    {user.isProvider && (
      <Badge variant="default" size="sm">
        Provider
      </Badge>
    )}
    {user.chargesEnabled && (
      <Badge variant="success" size="sm">
        Stripe
      </Badge>
    )}
  </div>
);

// ============================================================================
// User Detail Panel
// ============================================================================

interface UserDetailPanelProps {
  userId: number | null;
  onClose: () => void;
}

const UserDetailPanel: React.FC<UserDetailPanelProps> = ({
  userId,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user, loading, error } = useUserDetail(userId);

  if (!userId) {
    return (
      <div className="h-full flex items-center justify-center border-l border-border bg-card">
        <div className="text-center px-6">
          <UsersIcon className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Select a user to view details
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center border-l border-border bg-card">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="h-full flex items-center justify-center border-l border-border bg-card">
        <div className="text-center px-6">
          <UserX className="h-12 w-12 text-destructive/30 mx-auto mb-3" />
          <p className="text-sm text-destructive">{error || 'User not found'}</p>
        </div>
      </div>
    );
  }

  const metrics = user.metrics;

  return (
    <div className="h-full flex flex-col border-l border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        <h3 className="text-sm font-semibold text-foreground">User Detail</h3>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => navigate(`/users/${userId}`)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            title="Open full view"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Full View
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors lg:hidden"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-5">
        {/* Profile header */}
        <div className="flex items-center gap-3">
          <Avatar
            src={user.profileImage || undefined}
            name={user.name || 'User'}
            size="lg"
          />
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-foreground truncate">
              {user.name || 'No name'}
            </p>
            <p className="text-xs text-muted-foreground">ID: {user.id}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {user.isActive ? (
                <Badge variant="success" size="sm">
                  Active
                </Badge>
              ) : (
                <Badge variant="error" size="sm">
                  Inactive
                </Badge>
              )}
              {user.isVerified && (
                <Badge variant="info" size="sm">
                  Verified
                </Badge>
              )}
              {user.isProvider && (
                <Badge variant="default" size="sm">
                  Provider
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Contact info */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Contact
          </h4>
          {user.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground truncate">{user.email}</span>
            </div>
          )}
          {user.phone && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground">
                {formatPhoneNumber(user.phone)}
              </span>
            </div>
          )}
          {user.bio && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {user.bio}
            </p>
          )}
        </div>

        {/* Profile details */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Profile
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {user.gender && (
              <div>
                <span className="text-muted-foreground">Gender:</span>{' '}
                <span className="text-foreground">{user.gender}</span>
              </div>
            )}
            {user.pronouns && (
              <div>
                <span className="text-muted-foreground">Pronouns:</span>{' '}
                <span className="text-foreground">{user.pronouns}</span>
              </div>
            )}
            {user.dateOfBirth && (
              <div>
                <span className="text-muted-foreground">DOB:</span>{' '}
                <span className="text-foreground">
                  {parseDateOnly(user.dateOfBirth).toLocaleDateString()}
                </span>
              </div>
            )}
            {user.referralCode && (
              <div>
                <span className="text-muted-foreground">Referral:</span>{' '}
                <span className="text-foreground font-mono">
                  {user.referralCode}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Ratings */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Ratings
          </h4>
          <div className="grid grid-cols-3 gap-2">
            <DetailMetricCard
              label="Overall"
              value={user.rating != null ? user.rating.toFixed(1) : 'N/A'}
              icon={Star}
              color="text-yellow-400"
            />
            <DetailMetricCard
              label="As Customer"
              value={
                user.customerRating != null
                  ? user.customerRating.toFixed(1)
                  : 'N/A'
              }
              icon={Star}
              color="text-blue-400"
            />
            <DetailMetricCard
              label="As Provider"
              value={
                user.providerRating != null
                  ? user.providerRating.toFixed(1)
                  : 'N/A'
              }
              icon={Star}
              color="text-emerald-400"
            />
          </div>
        </div>

        {/* Social */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Social
          </h4>
          <div className="grid grid-cols-3 gap-2">
            <DetailMetricCard
              label="Reviews"
              value={formatNumber(user.totalReviews)}
              icon={Star}
              color="text-yellow-400"
            />
            <DetailMetricCard
              label="Followers"
              value={formatNumber(user.followerCount)}
              icon={Heart}
              color="text-red-400"
            />
            <DetailMetricCard
              label="Connections"
              value={formatNumber(user.connectionCount)}
              icon={UsersIcon}
              color="text-primary"
            />
          </div>
        </div>

        {/* Booking metrics */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Bookings
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <DetailMetricCard
              label="As Customer"
              value={formatNumber(metrics.bookingsAsCustomerCount)}
              icon={Calendar}
              color="text-blue-400"
              subtitle={`${formatNumber(
                metrics.completedBookingsAsCustomer
              )} completed`}
            />
            <DetailMetricCard
              label="As Provider"
              value={formatNumber(metrics.bookingsAsProviderCount)}
              icon={Briefcase}
              color="text-emerald-400"
              subtitle={`${formatNumber(
                metrics.completedBookingsAsProvider
              )} completed`}
            />
            <DetailMetricCard
              label="Total Spent"
              value={formatCents(metrics.totalSpentCents)}
              icon={CreditCard}
              color="text-orange-400"
            />
            <DetailMetricCard
              label="Total Earned"
              value={formatCents(metrics.totalEarnedCents)}
              icon={CreditCard}
              color="text-emerald-400"
            />
          </div>
        </div>

        {/* Content metrics */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Content
          </h4>
          <div className="grid grid-cols-3 gap-2">
            <DetailMetricCard
              label="Media"
              value={formatNumber(metrics.mediaAssetsCount)}
              icon={Film}
              color="text-primary"
            />
            <DetailMetricCard
              label="Views"
              value={formatNumber(metrics.contentViewsTotal)}
              icon={Eye}
              color="text-blue-400"
            />
            <DetailMetricCard
              label="Likes"
              value={formatNumber(metrics.contentLikesTotal)}
              icon={Heart}
              color="text-red-400"
            />
          </div>
        </div>

        {/* Stripe */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Payment
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Customer ID:</span>{' '}
              <span className="text-foreground font-mono text-[10px]">
                {user.stripeCustomerId || 'None'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Account ID:</span>{' '}
              <span className="text-foreground font-mono text-[10px]">
                {user.stripeAccountId || 'None'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Charges:</span>{' '}
              <span
                className={
                  user.chargesEnabled
                    ? 'text-emerald-400'
                    : 'text-muted-foreground'
                }
              >
                {user.chargesEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Payouts:</span>{' '}
              <span
                className={
                  user.payoutsEnabled
                    ? 'text-emerald-400'
                    : 'text-muted-foreground'
                }
              >
                {user.payoutsEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        {/* Services */}
        {user.services && user.services.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Services ({user.services.length})
            </h4>
            <div className="space-y-1">
              {user.services.map((svc) => (
                <div
                  key={svc.id}
                  className="flex items-center justify-between text-xs p-2 rounded-lg bg-muted/20"
                >
                  <span className="text-foreground truncate flex-1">
                    {svc.serviceName}
                  </span>
                  <span className="text-muted-foreground ml-2 flex-shrink-0">
                    {svc.isFree ? 'Free' : `$${svc.pricePerHour}/hr`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Dates
          </h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Joined</span>
              <span className="text-foreground">
                {formatDateTime(user.createdAt)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Seen</span>
              <span className="text-foreground">
                {user.lastSeenAt ? formatDateTime(user.lastSeenAt) : 'Never'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Updated</span>
              <span className="text-foreground">
                {formatDateTime(user.updatedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Detail Metric Card (small, for user detail panel)
// ============================================================================

interface DetailMetricCardProps {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}

const DetailMetricCard: React.FC<DetailMetricCardProps> = ({
  label,
  value,
  icon: Icon,
  color,
  subtitle,
}) => (
  <div className="p-2 rounded-lg bg-muted/20 border border-border/50">
    <div className="flex items-center gap-1.5 mb-0.5">
      <Icon className={cn('h-3 w-3', color)} />
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
    <p className="text-sm font-semibold text-foreground">{value}</p>
    {subtitle && (
      <p className="text-[10px] text-muted-foreground">{subtitle}</p>
    )}
  </div>
);

// ============================================================================
// Section Header
// ============================================================================

interface SectionHeaderProps {
  title: string;
  icon: React.ElementType;
  subtitle?: string;
  children?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  icon: Icon,
  subtitle,
  children,
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-primary" />
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {subtitle && (
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      )}
    </div>
    {children}
  </div>
);

// ============================================================================
// Most Active Users Card
// ============================================================================

interface SpotlightUserCardProps {
  user: UserListItem;
  rank: number;
  onClick: (id: number) => void;
}

const SpotlightUserCard: React.FC<SpotlightUserCardProps> = ({
  user,
  rank,
  onClick,
}) => {
  // Engagement score: followers + connections + reviews
  const engagement = user.followerCount + user.connectionCount + user.totalReviews;

  return (
    <button
      onClick={() => onClick(user.id)}
      className="card p-3 text-left hover:border-primary/40 transition-all duration-200 hover:shadow-glow-sm group"
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar
            src={user.profileImage || undefined}
            name={user.name || 'User'}
            size="md"
          />
          <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <span className="text-[9px] font-bold text-white">
              {rank}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
            {user.name || 'No name'}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {user.rating != null && user.rating > 0 && (
              <div className="flex items-center gap-0.5">
                <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                <span className="text-[10px] font-medium text-foreground">
                  {user.rating.toFixed(1)}
                </span>
              </div>
            )}
            <span className="text-[10px] text-muted-foreground">
              {formatCompactNumber(engagement)} engagement
            </span>
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {user.isProvider && (
              <Badge variant="default" size="sm">
                Provider
              </Badge>
            )}
            {user.isVerified && (
              <Badge variant="info" size="sm">
                Verified
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-0.5">
            <Heart className="h-3 w-3" />
            {formatCompactNumber(user.followerCount)}
          </span>
          <span className="flex items-center gap-0.5">
            <UsersIcon className="h-3 w-3" />
            {formatCompactNumber(user.connectionCount)}
          </span>
          <span className="flex items-center gap-0.5">
            <Star className="h-3 w-3" />
            {formatNumber(user.totalReviews)}
          </span>
        </div>
        {user.lastSeenAt && (
          <span className="text-[10px] text-muted-foreground/60">
            Seen {formatRelativeTime(user.lastSeenAt)}
          </span>
        )}
      </div>
    </button>
  );
};

// ============================================================================
// Top Provider Row
// ============================================================================

interface TopProviderRowProps {
  user: UserListItem;
  rank: number;
  maxRating: number;
  onClick: (id: number) => void;
}

const TopProviderRow: React.FC<TopProviderRowProps> = ({
  user,
  rank,
  maxRating,
  onClick,
}) => (
  <button
    onClick={() => onClick(user.id)}
    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/20 transition-colors w-full text-left"
  >
    <span className="text-xs text-muted-foreground w-5 text-right flex-shrink-0">
      {rank}.
    </span>
    <Avatar
      src={user.profileImage || undefined}
      name={user.name || 'User'}
      size="sm"
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-foreground truncate">
        {user.name || 'No name'}
      </p>
      <div className="flex items-center gap-1 mt-0.5">
        <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
        <span className="text-xs text-foreground">
          {user.rating?.toFixed(1) || '0.0'}
        </span>
        <span className="text-[10px] text-muted-foreground">
          ({formatNumber(user.totalReviews)} reviews)
        </span>
      </div>
    </div>
    <div className="w-20 h-1.5 rounded-full bg-muted/20 overflow-hidden flex-shrink-0">
      <div
        className="h-full bg-yellow-400 rounded-full"
        style={{
          width: `${maxRating > 0 ? ((user.rating || 0) / maxRating) * 100 : 0}%`,
        }}
      />
    </div>
  </button>
);

// ============================================================================
// Recent Signup Row
// ============================================================================

interface RecentSignupRowProps {
  user: UserListItem;
  onClick: (id: number) => void;
}

const RecentSignupRow: React.FC<RecentSignupRowProps> = ({ user, onClick }) => (
  <button
    onClick={() => onClick(user.id)}
    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/20 transition-colors w-full text-left"
  >
    <Avatar
      src={user.profileImage || undefined}
      name={user.name || 'User'}
      size="sm"
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-foreground truncate">
        {user.name || 'No name'}
      </p>
      {user.email && (
        <p className="text-[10px] text-muted-foreground truncate">
          {user.email}
        </p>
      )}
    </div>
    <div className="flex flex-col items-end flex-shrink-0">
      <span className="text-[10px] text-muted-foreground">
        {formatRelativeTime(user.createdAt)}
      </span>
      <div className="flex gap-1 mt-0.5">
        {user.isProvider && (
          <Badge variant="default" size="sm">
            Provider
          </Badge>
        )}
        {user.isVerified && (
          <Badge variant="info" size="sm">
            Verified
          </Badge>
        )}
      </div>
    </div>
  </button>
);

// ============================================================================
// Users Page (Analytics Dashboard)
// ============================================================================

const UsersPage: React.FC = () => {
  const navigate = useNavigate();

  // ---- Analytics state ----
  const [analyticsPreset, setAnalyticsPreset] = useState<DatePreset>('30d');
  const analyticsDateRange = useMemo(
    () => getDateRange(analyticsPreset),
    [analyticsPreset]
  );

  // ---- Analytics data ----
  const {
    data: analyticsData,
    loading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useUsersAnalytics(analyticsDateRange);

  // ---- Spotlight data: most engaged users (by followers+connections, limit 100 to pick from) ----
  const spotlightParams = useMemo<UserListQueryParams>(
    () => ({
      sortBy: 'rating',
      sortOrder: 'desc',
      limit: 100,
      offset: 0,
    }),
    []
  );
  const {
    items: spotlightRawItems,
    loading: spotlightLoading,
  } = useUserList(spotlightParams);

  // Derive most active users (sorted by engagement score)
  const mostActiveUsers = useMemo(() => {
    if (!spotlightRawItems.length) return [];
    return [...spotlightRawItems]
      .map((u) => ({
        ...u,
        _engagement: u.followerCount + u.connectionCount + u.totalReviews,
      }))
      .sort((a, b) => b._engagement - a._engagement)
      .slice(0, 6);
  }, [spotlightRawItems]);

  // Derive top providers (providers sorted by rating)
  const topProviders = useMemo(() => {
    if (!spotlightRawItems.length) return [];
    return spotlightRawItems
      .filter((u) => u.isProvider && u.rating != null && u.rating > 0)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 8);
  }, [spotlightRawItems]);

  // ---- Recent signups ----
  const recentParams = useMemo<UserListQueryParams>(
    () => ({
      sortBy: 'createdAt',
      sortOrder: 'desc',
      limit: 8,
      offset: 0,
    }),
    []
  );
  const {
    items: recentSignups,
    loading: recentLoading,
  } = useUserList(recentParams);

  // ---- User list state (filtered/searchable) ----
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showUserList, setShowUserList] = useState(false);

  // Build query params for user list
  const queryParams = useMemo<UserListQueryParams>(
    () => ({
      search: filters.search,
      isActive: filters.isActive,
      isVerified: filters.isVerified,
      isProvider: filters.isProvider,
      hasStripeAccount: filters.hasStripeAccount,
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
      sortBy: filters.sortBy || 'createdAt',
      sortOrder: filters.sortOrder || 'desc',
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    }),
    [filters, currentPage]
  );

  const {
    items,
    total,
    page,
    totalPages,
    loading,
    error,
    refetch,
  } = useUserList(queryParams);

  // Handlers
  const handleSelectUser = useCallback((id: number) => {
    setSelectedUserId(id);
    setShowDetail(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setShowDetail(false);
    setSelectedUserId(null);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value || undefined }));
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof UserFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handleSortChange = useCallback(
    (sortBy: UserFilters['sortBy']) => {
      setFilters((prev) => {
        if (prev.sortBy === sortBy) {
          return {
            ...prev,
            sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
          };
        }
        return { ...prev, sortBy, sortOrder: 'desc' };
      });
      setCurrentPage(1);
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setCurrentPage(1);
  }, []);

  const hasActiveFilters =
    filters.isActive !== undefined ||
    filters.isVerified !== undefined ||
    filters.isProvider !== undefined ||
    filters.hasStripeAccount !== undefined ||
    filters.createdAfter !== undefined ||
    filters.createdBefore !== undefined;

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const onSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        handleSearchChange(value);
      }, 400);
    },
    [handleSearchChange]
  );

  // ---- Computed analytics values ----
  const verifiedPct =
    analyticsData && analyticsData.totalUsers > 0
      ? ((analyticsData.verifiedUsers / analyticsData.totalUsers) * 100).toFixed(1)
      : '0';

  const activePct =
    analyticsData && analyticsData.totalUsers > 0
      ? (
          ((analyticsData.activeUsersInPeriod || 0) / analyticsData.totalUsers) *
          100
        ).toFixed(1)
      : '0';

  const providerPct =
    analyticsData && analyticsData.totalUsers > 0
      ? (
          (analyticsData.providersCount / analyticsData.totalUsers) *
          100
        ).toFixed(1)
      : '0';

  // Donut chart segments
  const userTypeSegments: DonutSegment[] = analyticsData
    ? [
        { label: 'Providers', value: analyticsData.providersCount, color: '#f97316' },
        {
          label: 'Consumers',
          value: analyticsData.consumersOnlyCount,
          color: '#a855f7',
        },
      ]
    : [];

  const userStatusSegments: DonutSegment[] = analyticsData
    ? [
        { label: 'Verified', value: analyticsData.verifiedUsers, color: '#3b82f6' },
        {
          label: 'Unverified',
          value: analyticsData.totalUsers - analyticsData.verifiedUsers,
          color: '#64748b',
        },
      ]
    : [];

  // Stripe users count from spotlight data
  const stripeUsersCount = useMemo(
    () => spotlightRawItems.filter((u) => u.chargesEnabled || u.payoutsEnabled).length,
    [spotlightRawItems]
  );

  // Max rating for provider bar chart
  const maxProviderRating = topProviders.length > 0 ? (topProviders[0].rating || 5) : 5;

  return (
    <div className="h-full flex">
      {/* Main Content Area */}
      <div
        className={cn(
          'flex-1 flex flex-col min-w-0 overflow-hidden',
          showDetail ? 'hidden lg:flex' : 'flex'
        )}
      >
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="p-6 space-y-8">
            {/* ============================================================ */}
            {/* HEADER */}
            {/* ============================================================ */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  User Analytics
                </h1>
                <p className="text-muted-foreground text-sm mt-0.5">
                  User insights, growth metrics, and engagement analysis
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Date range selector */}
                <div className="relative">
                  <select
                    value={analyticsPreset}
                    onChange={(e) =>
                      setAnalyticsPreset(e.target.value as DatePreset)
                    }
                    className={cn(
                      'appearance-none pl-3 pr-8 py-2 rounded-lg border border-border bg-card',
                      'text-sm text-foreground cursor-pointer',
                      'focus:outline-none focus:ring-2 focus:ring-primary/50'
                    )}
                  >
                    {presets.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                </div>
                <button
                  onClick={() => {
                    refetchAnalytics();
                    refetch();
                  }}
                  disabled={analyticsLoading}
                  className="p-2 rounded-lg border border-border hover:bg-muted/30 transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw
                    className={cn(
                      'h-4 w-4 text-muted-foreground',
                      analyticsLoading && 'animate-spin'
                    )}
                  />
                </button>
              </div>
            </div>

            {/* ============================================================ */}
            {/* KPI STAT CARDS ROW */}
            {/* ============================================================ */}
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
              <StatCard
                label="Total Users"
                value={
                  analyticsData
                    ? formatNumber(analyticsData.totalUsers)
                    : '--'
                }
                icon={UsersIcon}
                color="text-primary"
                bgColor="bg-primary/10"
                loading={analyticsLoading}
                onClick={() => navigate('/users/all')}
              />
              <StatCard
                label="New Users"
                value={
                  analyticsData
                    ? formatNumber(analyticsData.newUsersInPeriod)
                    : '--'
                }
                icon={UserPlus}
                color="text-emerald-400"
                bgColor="bg-emerald-400/10"
                loading={analyticsLoading}
                subtitle={analyticsData ? `in selected period` : undefined}
                trend={
                  analyticsData && analyticsData.totalUsers > 0
                    ? {
                        value: parseFloat(
                          (
                            (analyticsData.newUsersInPeriod /
                              analyticsData.totalUsers) *
                            100
                          ).toFixed(1)
                        ),
                        label: 'of total',
                      }
                    : null
                }
              />
              <StatCard
                label="Active Users"
                value={
                  analyticsData
                    ? formatNumber(analyticsData.activeUsersInPeriod)
                    : '--'
                }
                icon={Activity}
                color="text-blue-400"
                bgColor="bg-blue-400/10"
                loading={analyticsLoading}
                subtitle={analyticsData ? `${activePct}% of total` : undefined}
              />
              <StatCard
                label="Verified"
                value={
                  analyticsData
                    ? formatNumber(analyticsData.verifiedUsers)
                    : '--'
                }
                icon={UserCheck}
                color="text-cyan-400"
                bgColor="bg-cyan-400/10"
                loading={analyticsLoading}
                subtitle={
                  analyticsData ? `${verifiedPct}% verified` : undefined
                }
              />
              <StatCard
                label="Providers"
                value={
                  analyticsData
                    ? formatNumber(analyticsData.providersCount)
                    : '--'
                }
                icon={Briefcase}
                color="text-orange-400"
                bgColor="bg-orange-400/10"
                loading={analyticsLoading}
                subtitle={
                  analyticsData ? `${providerPct}% of users` : undefined
                }
              />
              <StatCard
                label="Consumers"
                value={
                  analyticsData
                    ? formatNumber(analyticsData.consumersOnlyCount)
                    : '--'
                }
                icon={Users2}
                color="text-purple-400"
                bgColor="bg-purple-400/10"
                loading={analyticsLoading}
              />
            </div>

            {/* ============================================================ */}
            {/* CHARTS SECTION */}
            {/* ============================================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* User Growth Chart - takes 2 columns */}
              <div className="card p-4 lg:col-span-2">
                <SectionHeader
                  title="User Growth"
                  icon={TrendingUp}
                  subtitle={
                    analyticsData
                      ? `${formatNumber(analyticsData.newUsersInPeriod)} new users`
                      : ''
                  }
                />
                <div className="mt-4">
                  <AreaChart
                    data={analyticsData?.userGrowthTrend || []}
                    loading={analyticsLoading}
                    color="#2AC5F4"
                    height={180}
                  />
                </div>
              </div>

              {/* User Type Distribution - donut chart */}
              <div className="card p-4">
                <SectionHeader title="User Types" icon={PieChart} />
                <div className="mt-4 flex items-center justify-center">
                  <DonutChart
                    segments={userTypeSegments}
                    loading={analyticsLoading}
                    size={130}
                    strokeWidth={18}
                    centerValue={
                      analyticsData
                        ? formatCompactNumber(analyticsData.totalUsers)
                        : '--'
                    }
                    centerLabel="total"
                  />
                </div>
              </div>
            </div>

            {/* Second charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Verification Status */}
              <div className="card p-4">
                <SectionHeader title="Verification" icon={ShieldCheck} />
                <div className="mt-4 flex items-center justify-center">
                  <DonutChart
                    segments={userStatusSegments}
                    loading={analyticsLoading}
                    size={130}
                    strokeWidth={18}
                    centerValue={analyticsData ? `${verifiedPct}%` : '--'}
                    centerLabel="verified"
                  />
                </div>
              </div>

              {/* Provider Onboarding Progress */}
              <div className="card p-4 lg:col-span-2">
                <SectionHeader
                  title="Provider Onboarding"
                  icon={CreditCard}
                  subtitle={
                    analyticsData
                      ? `${formatNumber(analyticsData.providersCount)} providers`
                      : ''
                  }
                />
                <div className="mt-4 space-y-3">
                  {analyticsLoading ? (
                    <div className="h-20 bg-muted/20 rounded animate-pulse" />
                  ) : analyticsData?.providerOnboarding ? (
                    <>
                      <ProgressBar
                        label="Stripe Connected"
                        value={
                          analyticsData.providerOnboarding.stripeConnected ??
                          (analyticsData.providerOnboarding as any)?.stripeAccountCreated ??
                          0
                        }
                        total={analyticsData.providersCount}
                        color="bg-primary"
                      />
                      <ProgressBar
                        label="Charges Enabled"
                        value={
                          analyticsData.providerOnboarding.chargesEnabled ?? 0
                        }
                        total={analyticsData.providersCount}
                        color="bg-emerald-500"
                      />
                      <ProgressBar
                        label="Payouts Enabled"
                        value={
                          analyticsData.providerOnboarding.payoutsEnabled ?? 0
                        }
                        total={analyticsData.providersCount}
                        color="bg-blue-500"
                      />
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No onboarding data
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ============================================================ */}
            {/* SPOTLIGHT SECTIONS */}
            {/* ============================================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Most Active Users */}
              <div className="lg:col-span-2 space-y-3">
                <SectionHeader
                  title="Most Active Users"
                  icon={Zap}
                  subtitle="By engagement score"
                />
                {spotlightLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="card p-3 animate-pulse"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-muted/30 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-3/4 bg-muted/30 rounded" />
                            <div className="h-3 w-1/2 bg-muted/30 rounded" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : mostActiveUsers.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {mostActiveUsers.map((user, i) => (
                      <SpotlightUserCard
                        key={user.id}
                        user={user}
                        rank={i + 1}
                        onClick={handleSelectUser}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="card p-6 text-center">
                    <UsersIcon className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No user engagement data available
                    </p>
                  </div>
                )}
              </div>

              {/* Top Providers */}
              <div className="space-y-3">
                <SectionHeader
                  title="Top Rated Providers"
                  icon={Crown}
                  subtitle="By rating"
                />
                <div className="card p-3">
                  {spotlightLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 p-2 animate-pulse"
                        >
                          <div className="w-8 h-8 bg-muted/30 rounded-full" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3.5 w-2/3 bg-muted/30 rounded" />
                            <div className="h-3 w-1/3 bg-muted/30 rounded" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : topProviders.length > 0 ? (
                    <div className="space-y-0.5">
                      {topProviders.map((user, i) => (
                        <TopProviderRow
                          key={user.id}
                          user={user}
                          rank={i + 1}
                          maxRating={maxProviderRating}
                          onClick={handleSelectUser}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <Briefcase className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">
                        No providers found
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Signups */}
            <div className="space-y-3">
              <SectionHeader
                title="Recent Signups"
                icon={UserPlus}
                subtitle="Newest members"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {recentLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="card p-3 animate-pulse">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-muted/30 rounded-full" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3.5 w-2/3 bg-muted/30 rounded" />
                            <div className="h-3 w-1/2 bg-muted/30 rounded" />
                          </div>
                        </div>
                      </div>
                    ))
                  : recentSignups.slice(0, 8).map((user) => (
                      <div key={user.id} className="card p-3">
                        <RecentSignupRow
                          user={user}
                          onClick={handleSelectUser}
                        />
                      </div>
                    ))}
              </div>
            </div>

            {/* ============================================================ */}
            {/* USER LIST SECTION */}
            {/* ============================================================ */}
            <div className="space-y-3">
              {/* Toggle & Search Header */}
              <div className="flex items-center justify-between">
                <SectionHeader
                  title="All Users"
                  icon={UsersIcon}
                  subtitle={
                    !loading
                      ? `${formatNumber(total)} total`
                      : ''
                  }
                />
                <button
                  onClick={() => setShowUserList(!showUserList)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    showUserList
                      ? 'bg-primary/15 text-primary'
                      : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted/30'
                  )}
                >
                  <BarChart3 className="h-4 w-4" />
                  {showUserList ? 'Collapse' : 'Expand List'}
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      showUserList && 'rotate-180'
                    )}
                  />
                </button>
              </div>

              {showUserList && (
                <div className="card overflow-hidden animate-slide-up">
                  {/* Search + Filter bar */}
                  <div className="p-4 border-b border-border space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search by name, email, phone, or ID..."
                          value={searchInput}
                          onChange={onSearchInput}
                          className={cn(
                            'w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background',
                            'text-sm text-foreground placeholder:text-muted-foreground',
                            'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                          )}
                        />
                      </div>
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                          showFilters || hasActiveFilters
                            ? 'border-primary/40 bg-primary/10 text-primary'
                            : 'border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted/30'
                        )}
                      >
                        <Filter className="h-4 w-4" />
                        Filters
                        {hasActiveFilters && (
                          <span className="ml-1 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                            {[
                              filters.isActive,
                              filters.isVerified,
                              filters.isProvider,
                              filters.hasStripeAccount,
                              filters.createdAfter,
                              filters.createdBefore,
                            ].filter((v) => v !== undefined).length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => refetch()}
                        disabled={loading}
                        className="p-2 rounded-lg border border-border hover:bg-muted/30 transition-colors disabled:opacity-50"
                        title="Refresh"
                      >
                        <RefreshCw
                          className={cn(
                            'h-4 w-4 text-muted-foreground',
                            loading && 'animate-spin'
                          )}
                        />
                      </button>
                    </div>

                    {/* Filter panel */}
                    {showFilters && (
                      <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-muted/10 border border-border/50">
                        {/* Status filter */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase">
                            Status
                          </label>
                          <select
                            value={filters.isActive ?? ''}
                            onChange={(e) =>
                              handleFilterChange(
                                'isActive',
                                e.target.value || undefined
                              )
                            }
                            className={cn(
                              'px-2 py-1.5 rounded-lg border border-border bg-background',
                              'text-xs text-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-primary/50'
                            )}
                          >
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </div>

                        {/* Verified filter */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase">
                            Verified
                          </label>
                          <select
                            value={filters.isVerified ?? ''}
                            onChange={(e) =>
                              handleFilterChange(
                                'isVerified',
                                e.target.value || undefined
                              )
                            }
                            className={cn(
                              'px-2 py-1.5 rounded-lg border border-border bg-background',
                              'text-xs text-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-primary/50'
                            )}
                          >
                            <option value="">All</option>
                            <option value="true">Verified</option>
                            <option value="false">Not Verified</option>
                          </select>
                        </div>

                        {/* Provider filter */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase">
                            Provider
                          </label>
                          <select
                            value={filters.isProvider ?? ''}
                            onChange={(e) =>
                              handleFilterChange(
                                'isProvider',
                                e.target.value || undefined
                              )
                            }
                            className={cn(
                              'px-2 py-1.5 rounded-lg border border-border bg-background',
                              'text-xs text-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-primary/50'
                            )}
                          >
                            <option value="">All</option>
                            <option value="true">Providers</option>
                            <option value="false">Consumers Only</option>
                          </select>
                        </div>

                        {/* Stripe filter */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase">
                            Stripe
                          </label>
                          <select
                            value={filters.hasStripeAccount ?? ''}
                            onChange={(e) =>
                              handleFilterChange(
                                'hasStripeAccount',
                                e.target.value || undefined
                              )
                            }
                            className={cn(
                              'px-2 py-1.5 rounded-lg border border-border bg-background',
                              'text-xs text-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-primary/50'
                            )}
                          >
                            <option value="">All</option>
                            <option value="true">Has Stripe</option>
                            <option value="false">No Stripe</option>
                          </select>
                        </div>

                        {/* Created after */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase">
                            Created After
                          </label>
                          <input
                            type="date"
                            value={filters.createdAfter ?? ''}
                            onChange={(e) =>
                              handleFilterChange(
                                'createdAfter',
                                e.target.value || undefined
                              )
                            }
                            className={cn(
                              'px-2 py-1.5 rounded-lg border border-border bg-background',
                              'text-xs text-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-primary/50'
                            )}
                          />
                        </div>

                        {/* Created before */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase">
                            Created Before
                          </label>
                          <input
                            type="date"
                            value={filters.createdBefore ?? ''}
                            onChange={(e) =>
                              handleFilterChange(
                                'createdBefore',
                                e.target.value || undefined
                              )
                            }
                            className={cn(
                              'px-2 py-1.5 rounded-lg border border-border bg-background',
                              'text-xs text-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-primary/50'
                            )}
                          />
                        </div>

                        {/* Clear filters */}
                        {hasActiveFilters && (
                          <button
                            onClick={handleClearFilters}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Sort bar + count */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                    <span className="text-xs text-muted-foreground">
                      {loading
                        ? 'Loading...'
                        : `${formatNumber(total)} user${total !== 1 ? 's' : ''}`}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-muted-foreground mr-1">
                        Sort:
                      </span>
                      {(
                        ['createdAt', 'name', 'rating', 'lastSeenAt'] as const
                      ).map((field) => (
                        <button
                          key={field}
                          onClick={() => handleSortChange(field)}
                          className={cn(
                            'px-2 py-1 rounded text-[10px] font-medium transition-colors',
                            (filters.sortBy || 'createdAt') === field
                              ? 'bg-primary/15 text-primary'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                          )}
                        >
                          {field === 'createdAt'
                            ? 'Date'
                            : field === 'lastSeenAt'
                            ? 'Last Seen'
                            : field.charAt(0).toUpperCase() + field.slice(1)}
                          {(filters.sortBy || 'createdAt') === field && (
                            <span className="ml-0.5">
                              {(filters.sortOrder || 'desc') === 'asc'
                                ? '\u2191'
                                : '\u2193'}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* User rows */}
                  <div className="max-h-[600px] overflow-y-auto scrollbar-thin">
                    {loading ? (
                      <div className="space-y-0">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-4 px-4 py-3 border-b border-border/50 animate-pulse"
                          >
                            <div className="w-10 h-10 bg-muted/30 rounded-full flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                              <div className="h-4 w-1/3 bg-muted/30 rounded" />
                              <div className="h-3 w-1/2 bg-muted/30 rounded" />
                            </div>
                            <div className="h-5 w-16 bg-muted/30 rounded-full" />
                          </div>
                        ))}
                      </div>
                    ) : error ? (
                      <div className="flex flex-col items-center justify-center py-16 px-4">
                        <UserX className="h-10 w-10 text-destructive/30 mb-3" />
                        <p className="text-sm text-destructive">{error}</p>
                        <button
                          onClick={() => refetch()}
                          className="mt-3 px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    ) : items.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 px-4">
                        <UsersIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
                        <p className="text-sm text-muted-foreground">
                          No users found
                        </p>
                        <p className="text-xs text-muted-foreground/70 mt-1">
                          Try adjusting your search or filters
                        </p>
                      </div>
                    ) : (
                      <div>
                        {items.map((user) => {
                          const isSelected = selectedUserId === user.id;
                          return (
                            <button
                              key={user.id}
                              onClick={() => handleSelectUser(user.id)}
                              className={cn(
                                'w-full flex items-center gap-4 px-4 py-3 border-b border-border/50',
                                'text-left transition-colors',
                                isSelected
                                  ? 'bg-primary/10 border-l-2 border-l-primary'
                                  : 'hover:bg-muted/30 border-l-2 border-l-transparent'
                              )}
                            >
                              {/* Avatar */}
                              <Avatar
                                src={user.profileImage || undefined}
                                name={user.name || 'User'}
                                size="md"
                              />

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-foreground truncate">
                                    {user.name || 'No name'}
                                  </p>
                                  <span className="text-[10px] text-muted-foreground/60">
                                    #{user.id}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-0.5">
                                  {user.email && (
                                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                      {user.email}
                                    </span>
                                  )}
                                  {user.phone && (
                                    <span className="text-xs text-muted-foreground hidden sm:inline">
                                      {formatPhoneNumber(user.phone)}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1">
                                  <UserStatusBadges user={user} />
                                </div>
                              </div>

                              {/* Right side metrics */}
                              <div className="hidden md:flex flex-col items-end gap-1 flex-shrink-0">
                                {user.rating != null && user.rating > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                                    <span className="text-xs font-medium text-foreground">
                                      {user.rating.toFixed(1)}
                                    </span>
                                    {user.totalReviews > 0 && (
                                      <span className="text-[10px] text-muted-foreground">
                                        ({formatNumber(user.totalReviews)})
                                      </span>
                                    )}
                                  </div>
                                )}
                                <span className="text-[10px] text-muted-foreground">
                                  Joined {formatRelativeTime(user.createdAt)}
                                </span>
                                {user.lastSeenAt && (
                                  <span className="text-[10px] text-muted-foreground/60">
                                    Seen{' '}
                                    {formatRelativeTime(user.lastSeenAt)}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page <= 1}
                        className={cn(
                          'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors',
                          page <= 1
                            ? 'text-muted-foreground/30 cursor-not-allowed'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        )}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Prev
                      </button>
                      <div className="flex items-center gap-1">
                        {generatePageNumbers(page, totalPages).map((p, i) =>
                          p === -1 ? (
                            <span
                              key={`ellipsis-${i}`}
                              className="px-1 text-xs text-muted-foreground"
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => handlePageChange(p)}
                              className={cn(
                                'w-8 h-8 rounded-lg text-xs font-medium transition-colors',
                                p === page
                                  ? 'bg-primary/15 text-primary border border-primary/30'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                              )}
                            >
                              {p}
                            </button>
                          )
                        )}
                      </div>
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages}
                        className={cn(
                          'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors',
                          page >= totalPages
                            ? 'text-muted-foreground/30 cursor-not-allowed'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        )}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      <div
        className={cn(
          'w-full lg:w-[420px] flex-shrink-0',
          showDetail ? 'flex' : 'hidden lg:flex'
        )}
      >
        <div className="w-full">
          <UserDetailPanel
            userId={selectedUserId}
            onClose={handleCloseDetail}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Pagination page number generator
// ============================================================================

function generatePageNumbers(current: number, total: number): number[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: number[] = [];

  // Always show first page
  pages.push(1);

  if (current > 3) {
    pages.push(-1); // ellipsis
  }

  // Show pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push(-1); // ellipsis
  }

  // Always show last page
  if (!pages.includes(total)) {
    pages.push(total);
  }

  return pages;
}

export default UsersPage;
